import { refreshCsrfToken, submitWithFreshCsrfToken } from './fresh_csrf';

/**
 * CSRF for edge-cached guest pages, page-wide.
 *
 * Guest pages opted into the Cloudflare cache (AnonymousConditionalGet#edge_cache_anonymous)
 * are rendered with no token at all: no csrf meta tags, no authenticity_token fields, and no
 * session cookie. They carry <meta name="csrf-fresh-url"> instead. Before the first POST, from
 * a form or from fetch(), this fetches a token for the visitor's own session (which starts that
 * session) and puts it where Rails looks: the meta tag, the form field, the X-CSRF-Token header.
 *
 * Pages rendered for a session (signed in, or after the first POST) have real tokens and no
 * marker, so this does nothing there.
 */

function freshUrl() {
  return document.querySelector('meta[name="csrf-fresh-url"]')?.content || null;
}

function ensureMeta(name, content) {
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    meta.content = content;
    document.head.appendChild(meta);
  }
  return meta;
}

function sameOrigin(url) {
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

function needsToken(form, submitter) {
  const method = (submitter?.getAttribute('formmethod') || form.getAttribute('method') || 'get').toLowerCase();
  if (method !== 'post') return false;

  const action = submitter?.getAttribute('formaction') || form.getAttribute('action') || window.location.href;
  return sameOrigin(action);
}

function ensureTokenField(form) {
  if (form.querySelector('input[name="authenticity_token"]')) return;

  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = 'authenticity_token';
  input.value = document.querySelector('meta[name="csrf-token"]')?.content || '';
  form.appendChild(input);
}

function installFormGuard(url) {
  // Capture phase, so the token is in place before Turbo or any controller sees the submit.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !needsToken(form, event.submitter)) return;

      ensureTokenField(form);
      submitWithFreshCsrfToken(event, url);
    },
    true,
  );
}

function installFetchGuard(url) {
  const originalFetch = window.fetch;
  if (typeof originalFetch !== 'function') return;

  window.fetch = async function edgePageCsrfFetch(input, init = {}) {
    const request = input instanceof Request ? input : null;
    const method = (init.method || request?.method || 'GET').toUpperCase();
    const target = request ? request.url : String(input);

    if (method === 'GET' || method === 'HEAD' || !sameOrigin(target)) {
      return originalFetch.call(this, input, init);
    }

    await refreshCsrfToken(url);
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    if (!token) return originalFetch.call(this, input, init);

    const headers = new Headers(init.headers || request?.headers || undefined);
    headers.set('X-CSRF-Token', token);
    if (request) return originalFetch.call(this, new Request(request, { ...init, headers }));
    return originalFetch.call(this, input, { ...init, headers });
  };
}

export function installEdgePageCsrf() {
  const url = freshUrl();
  if (!url || window.__edgePageCsrfInstalled) return;
  window.__edgePageCsrfInstalled = true;

  // Libraries (Turbo, our controllers) read these; fresh_csrf fills the token in.
  ensureMeta('csrf-param', 'authenticity_token');
  ensureMeta('csrf-token', '');

  installFormGuard(url);
  installFetchGuard(url);
}
